import { ref, Ref } from '@vue/composition-api';
import i18n, { i18n_t } from '@/locales/i18n';

// 引入工具类
import storage from '@/renderer/scripts/utils/storage';
import smh from '@/renderer/scripts/smh';
import tips from '@/renderer/scripts/utils/tips';
import router from '@/renderer/router';
import { dispatchRouter } from '@/renderer/scripts/utils/tools/dispatchRouter';
import { IS_BROWSER } from '@/common/constant/platform';
import { CONSTANT_URL } from '@/renderer/scripts/http';
import { encodeText } from '@/renderer/scripts/utils/tools/packages/encrypt';

type Handler = {
  tenantId: Ref<string>;
  corpId: Ref<string>;
  tenantDomain: Ref<string>;
  tenantIdList: Ref<string[]>;
  corpIdList: Ref<string[]>;
  tenantType: Ref<'tenantName' | 'corpId' | 'domain' | string>; // 对应 租户id、企业id、自定义域名
  methods: {
    initData: () => Promise<void>;
    openYufu: (url: string) => void;
    submit: () => Promise<void>;
    getYufuUrl: (manual: boolean) => Promise<string>;
    initTenantList: () => void;
    addCustomIdList: (key: string, tenantType: string) => void;
  };
};
export default function actionHandler(): Handler {
  // 企业租户id、domain下拉列表最多各存5项
  const listLimit = 5;
  const tenantId = ref('');
  const corpId = ref('');
  const tenantDomain = ref('');
  // 默认使用企业id
  const tenantType = ref('corpId');
  const tenantIdList: Ref<string[]> = ref([]);
  const corpIdList: Ref<string[]> = ref([]);
  const from = IS_BROWSER ? 'web' : 'pc';
  const methods = {
    async initData() {
      const domainPrefix = (router.currentRoute.query?.domain_prefix as string) || '';
      const { isCustomizedDomain } = CONSTANT_URL;
      // 自定义域名
      if (isCustomizedDomain || domainPrefix) {
        tenantType.value = 'domain';
        tenantDomain.value = isCustomizedDomain ? window.location.host : domainPrefix;
        const url = await methods.getYufuUrl(false);
        if (url) {
          methods.openYufu(url);
        }
      }
    },
    async openYufu(url: string) {
      dispatchRouter(url, IS_BROWSER ? 'location' : '_blank');
    },
    async submit() {
      const url = await methods.getYufuUrl();
      if (!url) return;
      methods.openYufu(url);
    },
    // 获取ssourl
    async getYufuUrl(manual = true) {
      let url = '';
      try {
        let tenantKey!: string;
        if (tenantType.value === 'tenantName') {
          tenantKey = tenantId.value;
        } else if (tenantType.value === 'domain') {
          tenantKey = tenantDomain.value;
        } else {
          tenantKey = corpId.value;
        }
        if (!tenantKey) {
          tips.warning(
            i18n_t('请先输入{slot1}', {
              slot1: tenantType.value === 'tenantName' ? i18n_t('租户ID') : i18n_t('企业ID'),
            }),
          );
          return '';
        }
        // encode一下避免特殊字符出现在url里不被识别
        tenantKey = encodeURIComponent(tenantKey);
        const type = tenantType.value;
        let path = IS_BROWSER ? `${window.location.pathname}${window.location.search}` : '';
        // 对path进行先加密再2次encodeURI
        if (path) {
          path = encodeURIComponent(encodeURIComponent(encodeText(path)));
        }
        const res = await smh.api.getYufuUrl({
          tenantKey,
          from,
          type,
          custom_state: path,
        });
        url = res.data.url;
        // 手动输入的 请求成功后本地记录租户id或domain
        if (manual) {
          methods.addCustomIdList(tenantKey, tenantType.value);
        }
      } catch (e: any) {
        tips.error(e);
        url = '';
      }
      return url;
    },
    // 租户id、domain操作记录
    async initTenantList() {
      const storeTenantIdList = storage.get<string[]>(storage.StoreKey.SSO_ID_LIST) || [];
      const storeCorpIdList = storage.get<string[]>(storage.StoreKey.SSO_CORPID_LIST) || [];
      tenantIdList.value = storeTenantIdList.filter((item: string) => typeof item === 'string');
      corpIdList.value = storeCorpIdList.filter((item: string) => typeof item === 'string');
      // 默认选中最新一条企业id
      if (tenantIdList.value.length > 0) {
        [tenantId.value] = tenantIdList.value;
      }
      if (corpIdList.value.length > 0) {
        [corpId.value] = corpIdList.value;
      }
    },
    addCustomIdList(key: string, tenantType: string) {
      const list = tenantType === 'tenantName' ? tenantIdList.value : corpIdList.value;
      const storeKey = tenantType === 'tenantName' ? storage.StoreKey.SSO_ID_LIST : storage.StoreKey.SSO_CORPID_LIST;
      if (!list.includes(key)) {
        list.unshift(key);
      }
      if (list.length > listLimit) {
        list.pop();
      }
      storage.set(storeKey, list);
    },
  };
  methods.initTenantList();
  return {
    tenantId,
    tenantDomain,
    corpId,
    tenantIdList,
    corpIdList,
    tenantType,
    methods,
  };
}
